/**
 * HTML 转 PDF 工具函数
 * 使用 html2canvas + jsPDF 将HTML内容导出为PDF
 */

import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

export interface HtmlToPdfOptions {
  filename?: string;
  pageSize?: 'a4' | 'letter';
  orientation?: 'portrait' | 'landscape';
  scale?: number;
  margin?: number;
  imageQuality?: number; // JPEG 图片质量 0-1，默认 0.8
  imageFormat?: 'png' | 'jpeg'; // 图片格式，默认 'jpeg' 以减小体积
}

// 页面尺寸配置 (单位: mm)
const PAGE_SIZES = {
  a4: { width: 210, height: 297 },
  letter: { width: 216, height: 279 },
};

/**
 * 将 oklch 或其他现代颜色格式转换为 rgba
 */
const convertColorToRgba = (color: string): string => {
  // 如果已经是 rgb/rgba/hex，直接返回
  if (color.startsWith('rgb') || color.startsWith('#')) {
    return color;
  }

  // 如果是 transparent 或空值
  if (color === 'transparent' || !color) {
    return 'transparent';
  }

  // 对于 oklch 或其他不支持的颜色格式，返回默认值
  if (
    color.includes('oklch') ||
    color.includes('lab') ||
    color.includes('lch')
  ) {
    return 'rgba(0, 0, 0, 1)';
  }

  return color;
};

/**
 * 递归处理元素及其子元素的颜色
 */
const processElementColors = (element: HTMLElement): void => {
  const computedStyle = window.getComputedStyle(element);

  // 获取并转换颜色
  const color = computedStyle.color;
  const bgColor = computedStyle.backgroundColor;
  const borderColor = computedStyle.borderColor;

  // 应用转换后的颜色
  if (color && (color.includes('oklch') || color.includes('lab'))) {
    element.style.color = '#000000';
  }
  if (bgColor && (bgColor.includes('oklch') || bgColor.includes('lab'))) {
    element.style.backgroundColor = '#ffffff';
  }
  if (
    borderColor &&
    (borderColor.includes('oklch') || borderColor.includes('lab'))
  ) {
    element.style.borderColor = '#000000';
  }

  // 处理子元素
  Array.from(element.children).forEach((child) => {
    if (child instanceof HTMLElement) {
      processElementColors(child);
    }
  });
};

/**
 * 创建纯净的元素副本，移除所有 Tailwind 类名中的 oklch 颜色
 */
const createCleanElement = (originalElement: HTMLElement): HTMLElement => {
  // 深克隆元素
  const clone = originalElement.cloneNode(true) as HTMLElement;

  // 处理克隆的元素
  processElementColors(clone);

  return clone;
};

/**
 * 将HTML元素转换为PDF
 */
export const exportHtmlToPdf = async (
  element: HTMLElement,
  options: HtmlToPdfOptions = {},
): Promise<Blob> => {
  const {
    filename = 'resume.pdf',
    pageSize = 'a4',
    orientation = 'portrait',
    scale = 2, // 缩放比例 2 以获得更清晰的文字
    margin = 10,
    imageQuality = 0.92, // JPEG 质量 0.92 以获得更清晰的图片
    imageFormat = 'jpeg', // 使用 JPEG 格式
  } = options;

  const pageDims = PAGE_SIZES[pageSize];
  const isLandscape = orientation === 'landscape';
  const pdfWidth = isLandscape ? pageDims.height : pageDims.width;
  const pdfHeight = isLandscape ? pageDims.width : pageDims.height;

  // 创建干净的元素副本
  const cleanElement = createCleanElement(element);

  // 临时添加到 DOM 以获取正确的计算样式
  cleanElement.style.position = 'fixed';
  cleanElement.style.left = '-9999px';
  cleanElement.style.top = '0';
  cleanElement.style.width = element.scrollWidth + 'px';
  cleanElement.style.height = element.scrollHeight + 'px';
  document.body.appendChild(cleanElement);

  try {
    // 使用 html2canvas 将HTML转换为canvas
    const canvas = await html2canvas(cleanElement, {
      scale,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
      windowWidth: cleanElement.scrollWidth,
      windowHeight: cleanElement.scrollHeight,
    });

    // 创建PDF
    const pdf = new jsPDF({
      orientation,
      unit: 'mm',
      format: pageSize,
    });

    // 计算图片在PDF中的尺寸
    // 使用 JPEG 格式以减小文件体积，质量可配置
    const imgData = canvas.toDataURL(
      imageFormat === 'jpeg' ? 'image/jpeg' : 'image/png',
      imageQuality
    );
    const imgWidth = pdfWidth - margin * 2;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    // 分页处理
    let heightLeft = imgHeight;
    let position = 0;

    // 添加第一页
    // 根据图片格式选择对应的 jsPDF 格式标识
    const pdfImageFormat = imageFormat === 'jpeg' ? 'JPEG' : 'PNG';
    pdf.addImage(imgData, pdfImageFormat, margin, margin, imgWidth, imgHeight);
    heightLeft -= pdfHeight - margin * 2;

    // 如果内容超过一页，添加更多页面
    while (heightLeft > 0) {
      position = heightLeft - imgHeight + margin;
      pdf.addPage();
      pdf.addImage(imgData, pdfImageFormat, margin, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight - margin * 2;
    }

    // 返回PDF blob
    return pdf.output('blob');
  } finally {
    // 清理临时元素
    if (cleanElement.parentNode) {
      document.body.removeChild(cleanElement);
    }
  }
};

/**
 * 下载PDF文件
 */
export const downloadPdf = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * 预览PDF (在新窗口打开)
 */
export const previewPdf = (blob: Blob): void => {
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
};

/**
 * 将多个HTML页面合并为一个PDF
 */
export const exportMultiplePagesToPdf = async (
  pages: HTMLElement[],
  options: HtmlToPdfOptions = {},
): Promise<Blob> => {
  const {
    filename = 'resume.pdf',
    pageSize = 'a4',
    orientation = 'portrait',
    scale = 2, // 缩放比例 2 以获得更清晰的文字
    margin = 10,
    imageQuality = 0.92, // JPEG 质量 0.92 以获得更清晰的图片
    imageFormat = 'jpeg', // 使用 JPEG 格式
  } = options;

  const pageDims = PAGE_SIZES[pageSize];
  const isLandscape = orientation === 'landscape';
  const pdfWidth = isLandscape ? pageDims.height : pageDims.width;
  const pdfHeight = isLandscape ? pageDims.width : pageDims.height;

  // 创建PDF
  const pdf = new jsPDF({
    orientation,
    unit: 'mm',
    format: pageSize,
  });

  for (let i = 0; i < pages.length; i++) {
    const page = pages[i];

    // 创建干净的元素副本
    const cleanElement = createCleanElement(page);
    cleanElement.style.position = 'fixed';
    cleanElement.style.left = '-9999px';
    cleanElement.style.top = '0';
    cleanElement.style.width = page.scrollWidth + 'px';
    cleanElement.style.height = page.scrollHeight + 'px';
    document.body.appendChild(cleanElement);

    try {
      // 使用 html2canvas
      const canvas = await html2canvas(cleanElement, {
        scale,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        windowWidth: cleanElement.scrollWidth,
        windowHeight: cleanElement.scrollHeight,
      });

      // 使用 JPEG 格式以减小文件体积
      const imgData = canvas.toDataURL(
        imageFormat === 'jpeg' ? 'image/jpeg' : 'image/png',
        imageQuality
      );
      const imgWidth = pdfWidth - margin * 2;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // 添加新页面（除了第一页）
      if (i > 0) {
        pdf.addPage();
      }

      // 添加图片
      const pdfImageFormat = imageFormat === 'jpeg' ? 'JPEG' : 'PNG';
      pdf.addImage(imgData, pdfImageFormat, margin, margin, imgWidth, imgHeight);
    } finally {
      // 清理临时元素
      if (cleanElement.parentNode) {
        document.body.removeChild(cleanElement);
      }
    }
  }

  return pdf.output('blob');
};

/**
 * 打印HTML内容
 */
export const printHtml = (element: HTMLElement): void => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) return;

  // 创建干净的元素副本
  const cleanElement = createCleanElement(element);

  const styles = Array.from(document.styleSheets)
    .map((sheet) => {
      try {
        return Array.from(sheet.cssRules)
          .map((rule) => rule.cssText)
          .join('\n');
      } catch (e) {
        return '';
      }
    })
    .join('\n');

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>打印简历</title>
      <style>
        ${styles}
        @media print {
          body { margin: 0; padding: 20px; }
          .no-print { display: none !important; }
        }
      </style>
    </head>
    <body>
      ${cleanElement.outerHTML}
      <script>
        window.onload = function() {
          setTimeout(function() {
            window.print();
            window.close();
          }, 500);
        };
      </script>
    </body>
    </html>
  `);

  printWindow.document.close();
};
